// RouteDetails.jsx
import React, { useState, useEffect, useContext } from 'react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { ArrowLeft, Map, Clock, Users, Edit, User, CheckCircle, AlertTriangle, FileText } from 'lucide-react';
import { AuthContext } from "../../context/AuthContext";
import { fetchVigilantesPorServicio } from "../admin/firestoreService";
import AssignGuards from './AssignGuards';
import RouteRecords from './RouteRecords';
import './styles/routeDetails.scss';

const RouteDetails = ({ route, onBack, onEdit }) => {
  const [activeTab, setActiveTab] = useState('summary'); // 'summary', 'guards', 'records'
  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    completedCount: 0,
    missedCount: 0,
    inProgressCount: 0
  });

  const { currentService } = useContext(AuthContext);

  useEffect(() => {
    if (route && currentService) {
      fetchAssignedGuards();
      fetchRouteStats();
    }
  }, [route, currentService]);

  const fetchAssignedGuards = async () => {
    try {
      setLoading(true);
      
      // Obtener todos los vigilantes del servicio usando firestoreService
      const allGuards = await fetchVigilantesPorServicio(currentService);
      
      // Filtrar solo los vigilantes asignados a esta ruta
      const assignedGuards = allGuards.filter(guard => {
        const rutasAsignadas = guard.rutasAsignadas || [];
        return rutasAsignadas.includes(route.id);
      });

      setGuards(assignedGuards);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching assigned guards:', err);
      setLoading(false);
    }
  };

  const fetchRouteStats = async () => {
    try {
      // Obtener estadísticas de los registros de esta ruta desde la subcolección de registrosRuta
      const registrosRef = collection(db, 'servicios', currentService.id, 'registrosRuta');
      const q = query(registrosRef, where('rutaId', '==', route.id));
      const querySnapshot = await getDocs(q);

      // Contar los registros por estado
      let completed = 0;
      let missed = 0;
      let inProgress = 0;

      querySnapshot.docs.forEach(doc => {
        const registro = doc.data();
        if (registro.estado === 'completada') completed++;
        else if (registro.estado === 'perdida') missed++;
        else if (registro.estado === 'en-progreso') inProgress++;
      });

      setStats({
        completedCount: completed || 0, // Valores por defecto si no hay datos
        missedCount: missed || 0,
        inProgressCount: inProgress || 0
      });
    } catch (err) {
      console.error('Error fetching route stats:', err);
    }
  };

  const handleToggleActive = async () => {
    try {
      // Actualizar en la subcolección correcta
      const routeRef = doc(db, 'servicios', currentService.id, 'rutas', route.id);
      await updateDoc(routeRef, {
        active: !route.active,
        updatedAt: new Date()
      });

      // Actualizar localmente
      route.active = !route.active;
    } catch (err) {
      console.error('Error updating route status:', err);
      alert('Error al actualizar el estado de la ruta');
    }
  };

  return (
    <div className="route-details">
      <div className="details-header">
        <div className="header-left">
          <button
            onClick={onBack}
            className="back-button"
            title="Volver"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="route-info">
            <h2 className="route-name">{route.name}</h2>
            <div className="route-meta">
              <span className={`status-badge ${route.active ? 'active' : 'inactive'}`}>
                {route.active ? 'Activa' : 'Inactiva'}
              </span>
              {route.description && (
                <span className="route-description">{route.description}</span>
              )}
            </div>
          </div>
        </div>

        <div className="header-actions">
          <button
            onClick={handleToggleActive}
            className={`btn-toggle ${route.active ? 'btn-deactivate' : 'btn-activate'}`}
          >
            <User size={16} className="btn-icon" />
            {route.active ? 'Desactivar' : 'Activar'}
          </button>

          <button
            onClick={onEdit}
            className="btn-edit"
          >
            <Edit size={16} className="btn-icon" />
            Editar
          </button>
        </div>
      </div>

      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          <FileText size={18} className="tab-icon" />
          Resumen
        </button>
        <button
          className={`tab-button ${activeTab === 'guards' ? 'active' : ''}`}
          onClick={() => setActiveTab('guards')}
        >
          <Users size={18} className="tab-icon" />
          Usuarios
        </button>
        <button
          className={`tab-button ${activeTab === 'records' ? 'active' : ''}`}
          onClick={() => setActiveTab('records')}
        >
          <Clock size={18} className="tab-icon" />
          Registros
        </button>
      </div>

      <div className="details-content">
        {activeTab === 'summary' && (
          <div>
            <div className="stats-grid">
              <div className="stat-card completed">
                <div className="stat-info">
                  <p className="stat-label">Rondas Completadas</p>
                  <p className="stat-value">{stats.completedCount}</p>
                </div>
                <CheckCircle className="stat-icon" size={24} />
              </div>

              <div className="stat-card in-progress">
                <div className="stat-info">
                  <p className="stat-label">En Progreso</p>
                  <p className="stat-value">{stats.inProgressCount}</p>
                </div>
                <Clock className="stat-icon" size={24} />
              </div>

              <div className="stat-card missed">
                <div className="stat-info">
                  <p className="stat-label">Rondas Perdidas</p>
                  <p className="stat-value">{stats.missedCount}</p>
                </div>
                <AlertTriangle className="stat-icon" size={24} />
              </div>
            </div>

            <div className="info-grid">
              <div className="info-section">
                <h3 className="section-title">
                  <Map size={20} className="section-icon" />
                  Puntos de Control
                </h3>

                <div className="section-content">
                  {route.checkpoints?.length > 0 ? (
                    <div className="item-list">
                      {route.checkpoints.map((cp, index) => (
                        <div
                          key={cp.id || index}
                          className="list-item"
                        >
                          <div className="item-header">
                            <div>
                              <h4 className="item-title">{index + 1}. {cp.name}</h4>
                              {cp.location && (
                                <p className="item-subtitle">{cp.location}</p>
                              )}
                            </div>
                            <div className="item-coordinates">
                              <div>Lat: {cp.coordinates?.latitude}</div>
                              <div>Lng: {cp.coordinates?.longitude}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-message">No hay puntos de control definidos.</p>
                  )}
                </div>
              </div>

              <div className="info-section">
                <h3 className="section-title">
                  <Clock size={20} className="section-icon" />
                  Horarios
                </h3>

                <div className="section-content">
                  {route.schedule?.length > 0 ? (
                    <div className="item-list">
                      {route.schedule.map((day) => {
                        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                        const dayName = dayNames[day.dayOfWeek] || `Día ${day.dayOfWeek}`;

                        return (
                          <div
                            key={day.dayOfWeek}
                            className="list-item"
                          >
                            <h4 className="item-title">{dayName}</h4>
                            <div className="item-times">
                              {day.times?.map(time => (
                                <span
                                  key={time}
                                  className="time-badge"
                                >
                                  {time}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="empty-message">No hay horarios definidos.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'guards' && (
          <AssignGuards
            routeId={route.id}
            assignedGuards={guards}
            onUpdate={fetchAssignedGuards}
          />
        )}

        {activeTab === 'records' && (
          <RouteRecords routeId={route.id} />
        )}
      </div>
    </div>
  );
};

export default RouteDetails;